import { useLingui } from '@lingui/react/macro';
import { CalendarDays } from 'lucide-react';
import { useRef, useState, type ComponentProps } from 'react';

import {
  calendarDateToCivil,
  civilToCalendarDate,
  isCivilDate,
  parseDisplayDate,
  toDisplayDate,
  type CivilDate,
} from '@/shared/format/date';

import { Button } from './button';
import { Calendar } from './calendar';
import { cn } from './cn';
import { fieldControlClass } from './input';
import { slotModel } from './internal/mask-models';
import { useMaskedInput } from './internal/use-masked-input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

const TEMPLATE = '##/##/####';
const model = slotModel(() => TEMPLATE, (character) => character >= '0' && character <= '9', 8);

export type DateFieldProps = Omit<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max'> & {
  /** Data civil ISO (`2026-08-14`, {@link CivilDate}). Incompleta ou inválida volta como digitada (`14/08`), para o schema acusar. */
  readonly value: string | null | undefined;
  readonly onChange?: (value: string | null) => void;
  readonly min?: CivilDate;
  readonly max?: CivilDate;
};

function textToValue(significant: string): string | null {
  if (significant === '') return null;
  const text = model.render(significant);
  return significant.length === 8 ? (parseDisplayDate(text) ?? text) : text;
}

function valueToSignificant(value: string | null): string {
  if (value === null || value === '') return '';
  const display = isCivilDate(value) ? toDisplayDate(value) : value;
  return model.significant(display ?? '');
}

/**
 * Data em `dd/mm/aaaa`, digitada ou escolhida no calendário. O valor é data civil ISO, sem fuso
 * e sem `new Date()` sobre texto (U14); fuso e hora ficam no mapper.
 */
export function DateField({ value, onChange, onBlur, min, max, disabled, readOnly, className, placeholder, ...props }: DateFieldProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const bindings = useMaskedInput<string | null>({
    model,
    value: value ?? null,
    onChange,
    onBlur,
    toValue: textToValue,
    fromValue: valueToSignificant,
    sameValue: (left, right) => left === right,
  });
  const selected = value && isCivilDate(value) ? (civilToCalendarDate(value) ?? undefined) : undefined;
  const minDate = min ? civilToCalendarDate(min) : null;
  const maxDate = max ? civilToCalendarDate(max) : null;
  const blocked = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative" data-slot="date-field">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder={placeholder ?? t`dd/mm/aaaa`}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(fieldControlClass, 'pr-11 tabular-nums', className)}
            {...props}
            {...bindings}
          />
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled === true || readOnly === true}
              aria-label={t`Abrir calendário`}
              className="absolute inset-y-0 right-0 my-auto size-control rounded-l-none text-muted-foreground"
            >
              <CalendarDays aria-hidden />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="end"
        className="w-auto"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          bindings.ref.current?.focus();
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.querySelector<HTMLElement>('td button[tabindex="0"]')?.focus();
        }}
      >
        <Calendar
          mode="single"
          selected={selected}
          {...(selected ? { defaultMonth: selected } : {})}
          disabled={blocked}
          onSelect={(date) => {
            if (date) onChange?.(calendarDateToCivil(date));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
